import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { FormSection, DefinitionList, DataPair, DataTable } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { collectPhoneCheck, Counted, PhoneCheckReport } from '../../services/phoneCheckService';
import { formatBytes } from './formatBytes';
import './PhoneCheckPage.css';

type TableRow = PhoneCheckReport['tables'][number];

/**
 * Phone check — a read-only summary of what the app has stored on this phone,
 * for a farm admin to photograph and send to support. Reads only; see
 * collectPhoneCheck.
 */
const PhoneCheckPage: React.FC = () => {
  const { t } = useTranslation();
  const [report, setReport] = useState<PhoneCheckReport | null>(null);
  const [failed, setFailed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useBreadcrumb([{ label: t('phoneCheck.title') }]);

  useEffect(() => {
    let cancelled = false;
    collectPhoneCheck()
      .then(r => { if (!cancelled) setReport(r); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const unreadable = t('phoneCheck.couldNotRead');
  const count = (c: Counted) => (c.ok ? String(c.value) : unreadable);
  const bytes = (c: Counted) => (c.ok ? formatBytes(c.value) : unreadable);

  const meta = report
    ? t('phoneCheck.meta', { when: new Date(report.checkedAt).toLocaleString(), version: report.appVersion })
    : undefined;

  let body: React.ReactNode;
  if (failed) {
    body = <p>{unreadable}</p>;
  } else if (!report) {
    body = <p>{t('phoneCheck.checking')}</p>;
  } else {
    const notReported = t('phoneCheck.notReported');
    body = (
      <>
        <FormSection id="pc-space" title={t('phoneCheck.space.title')}>
          <DefinitionList columns={1} className="phone-check__pairs">
            <DataPair
              label={t('phoneCheck.space.used')}
              value={report.storage ? formatBytes(report.storage.usageBytes) : notReported}
            />
            <DataPair
              label={t('phoneCheck.space.allowed')}
              value={report.storage ? formatBytes(report.storage.quotaBytes) : notReported}
            />
            <DataPair label={t('phoneCheck.space.photoSize')} value={bytes(report.photos.totalBytes)} />
          </DefinitionList>
        </FormSection>

        <FormSection id="pc-scouting" title={t('phoneCheck.scouting.title')}>
          <DefinitionList columns={1} className="phone-check__pairs">
            <DataPair label={t('phoneCheck.scouting.waiting')} value={count(report.queue.pending)} />
            <DataPair label={t('phoneCheck.scouting.failed')} value={count(report.queue.failed)} />
            <DataPair label={t('phoneCheck.scouting.quarantined')} value={count(report.queue.quarantined)} />
            <DataPair label={t('phoneCheck.scouting.synced')} value={count(report.queue.synced)} />
            <DataPair label={t('phoneCheck.scouting.phoneDraftsOpen')} value={count(report.phoneDrafts.open)} />
            <DataPair label={t('phoneCheck.scouting.phoneDraftsFinished')} value={count(report.phoneDrafts.finished)} />
            <DataPair label={t('phoneCheck.scouting.deskDraftsOpen')} value={count(report.deskDrafts.open)} />
            <DataPair label={t('phoneCheck.scouting.deskDraftsFinished')} value={count(report.deskDrafts.finished)} />
            <DataPair label={t('phoneCheck.scouting.olderSessions')} value={count(report.compactSessions.total)} />
            <DataPair label={t('phoneCheck.scouting.olderFinished')} value={count(report.compactSessions.finished)} />
            <DataPair label={t('phoneCheck.scouting.photos')} value={count(report.photos.count)} />
          </DefinitionList>
        </FormSection>

        <FormSection id="pc-startup" title={t('phoneCheck.startup.title')}>
          <DefinitionList columns={1} className="phone-check__pairs">
            <DataPair
              label={t('phoneCheck.startup.duration')}
              value={
                report.startupSyncMs === null
                  ? t('phoneCheck.notMeasured')
                  : t('phoneCheck.startup.seconds', { value: (report.startupSyncMs / 1000).toFixed(1) })
              }
            />
            <DataPair
              label={t('phoneCheck.startup.connection')}
              value={report.online ? t('phoneCheck.startup.online') : t('phoneCheck.startup.offline')}
            />
          </DefinitionList>
        </FormSection>

        <FormSection id="pc-details" title={t('phoneCheck.details.title')}>
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            aria-expanded={showDetails}
            onClick={() => setShowDetails(s => !s)}
          >
            {showDetails ? t('phoneCheck.details.hide') : t('phoneCheck.details.show')}
          </button>
          {showDetails && (
            <DataTable<TableRow>
              columns={[
                { key: 'name', label: t('phoneCheck.details.table') },
                { key: 'count', label: t('phoneCheck.details.records'), align: 'right', render: r => count(r.count) },
              ]}
              rows={report.tables}
              rowKey={r => r.name}
            />
          )}
        </FormSection>
      </>
    );
  }

  return (
    <div data-testid="phone-check">
      <PageHeader title={t('phoneCheck.title')} meta={meta} />
      {body}
    </div>
  );
};

export default PhoneCheckPage;
